import React from "react";
import { Users, User, Circle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStaffById } from "@/components/pages/staff/hooks/useStaff";
import { useTeamById } from "@/components/pages/staff/hooks/useTeams";

type IdType = string | number | null;

interface AssignedStaffCellProps {
  taskId?: IdType;
  teamId?: IdType;
  staffId?: IdType;
  teamName?: string | null;
  staffName?: string | null;
  className?: string;
  teamClassName?: string;
  staffClassName?: string;
  notAssignedStaffClassName?: string;
  externalUse?: boolean;
  iconSize?: number;
  showCircle?: boolean;
}

const resolveTeamName = ({
  providedTeamName,
  fetchedTeamName,
  teamId,
}: {
  providedTeamName?: string | null;
  fetchedTeamName?: string | null;
  teamId?: IdType;
}) => {
  if (providedTeamName) {
    return providedTeamName;
  }

  if (fetchedTeamName) {
    return fetchedTeamName;
  }

  if (teamId) {
    return `Team ${teamId}`;
  }

  return null;
};

const resolveStaffName = ({
  providedStaffName,
  fetchedStaffData,
  staffId,
}: {
  providedStaffName?: string | null;
  fetchedStaffData?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  staffId?: IdType;
}) => {
  if (providedStaffName) {
    return providedStaffName;
  }

  if (fetchedStaffData) {
    return (
      `${fetchedStaffData.firstName || ""} ${fetchedStaffData.lastName || ""}`.trim() ||
      fetchedStaffData.email ||
      undefined
    );
  }

  if (staffId) {
    return `Staff ${staffId}`;
  }

  return undefined;
};

export const AssignedStaffCell: React.FC<AssignedStaffCellProps> = ({
  teamId,
  staffId,
  teamName: providedTeamName,
  staffName: providedStaffName,
  className,
  teamClassName,
  staffClassName,
  notAssignedStaffClassName,
  iconSize = 26,
  showCircle = true,
  externalUse,
}) => {
  const shouldFetchTeam = !!teamId && !providedTeamName;
  const shouldFetchStaff = !!staffId && !providedStaffName;

  const { data: teamData } = useTeamById(
    shouldFetchTeam ? teamId?.toString() || "" : "",
  );
  const { data: staffData } = useStaffById(
    shouldFetchStaff ? staffId?.toString() || "" : "",
  );

  const teamName = resolveTeamName({
    providedTeamName,
    fetchedTeamName: teamData?.name,
    teamId,
  });
  const staffName = resolveStaffName({
    providedStaffName,
    fetchedStaffData: staffData,
    staffId,
  });

  const hasTeam = !!teamId;
  const hasStaff = !!staffId;

  if (!hasTeam && !hasStaff) {
    return (
      <div className={cn("flex flex-col items-center gap-1", className)}>
        {!externalUse && showCircle && <Circle size={iconSize} />}
        <span
          className={cn(
            "text-sm font-medium " +
              (externalUse ? "text-black" : "text-red-600"),
            notAssignedStaffClassName,
          )}
        >
          {externalUse ? "Assign Staff" : "Unassigned"}
        </span>
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center gap-2", className)}>
      <div className="flex items-center justify-center">
        {hasTeam ? (
          <Users size={iconSize} className="text-muted-foreground" />
        ) : (
          <User size={iconSize} className="text-muted-foreground" />
        )}
      </div>
      <div className="flex flex-col">
        {hasTeam && (
          <span className={cn("text-sm font-medium", teamClassName)}>
            {teamName}
          </span>
        )}
        {hasStaff && (
          <span
            className={cn(
              "text-xs",
              hasTeam ? "text-muted-foreground" : "text-sm font-medium",
              staffClassName,
            )}
          >
            {staffName}
          </span>
        )}
      </div>
    </div>
  );
};

export default AssignedStaffCell;
